const SERVICES = [
  {
    num:   '01',
    icon:  'ri-file-text-line',
    title: 'Tax Lien Certificate Acquisition',
    description:
      'We identify, research, and acquire tax lien certificates on your behalf across CA, NV & AZ — targeting high-yield properties with verified delinquency status and clean title.',
    features: [
      'Fully managed county auction bidding',
      'Interest rates up to 18% annually',
      'Active coverage across CA, NV & AZ',
    ],
    accent: '#f59e0b',
  },
  {
    num:   '02',
    icon:  'ri-building-4-line',
    title: 'Tax Deed Acquisition',
    description:
      'We handle the full tax deed purchase process from auction research to winning bid — securing ownership rights to tax-foreclosed properties in your target markets.',
    features: [
      'Full legal property ownership transfer',
      'Pre-foreclosure targeting & analysis',
      'Deed recording & closing coordination',
    ],
    accent: '#f97316',
  },
  {
    num:   '03',
    icon:  'ri-search-eye-line',
    title: 'Property Research & Due Diligence',
    description:
      'Full title searches, lien stacking analysis, property condition assessments, and redemption period tracking before any acquisition so you invest with confidence.',
    features: [
      'Title & senior lien stack analysis',
      'Market valuation & equity assessment',
      'Risk-scored due diligence reports',
    ],
    accent: '#a3e635',
  },
  {
    num:   '04',
    icon:  'ri-calendar-check-line',
    title: 'Redemption Period Monitoring',
    description:
      'We track all active certificates through their redemption windows, alerting you to key deadlines and managing the transition from certificate to deed ownership.',
    features: [
      'Automated deadline & expiry alerts',
      'Real-time portfolio status dashboard',
      'Payoff collection & deed conversion',
    ],
    accent: '#e879f9',
  },
];

export default function OurServices() {
  return (
    <section className="bg-[#070c14] py-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">

        {/* ── Header ── */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
              <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase">
                What We Do For You
              </span>
            </div>
            <h2 className="font-playfair text-3xl md:text-4xl lg:text-[44px] font-bold text-white leading-tight">
              Our Services
            </h2>
            <p className="text-white/45 text-base mt-3 max-w-xl leading-relaxed">
              Four core acquisition services — each one handled entirely by our team
              so you can invest passively without industry experience.
            </p>
          </div>

          <a
            href="/membership-benefits"
            className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-semibold text-sm transition-colors duration-200 whitespace-nowrap self-start lg:self-end"
          >
            View all plan inclusions
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        {/* ── Cards grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {SERVICES.map((svc) => (
            <div
              key={svc.num}
              className="group relative bg-[#0d1520] rounded-2xl p-8 border border-white/6 hover:border-amber-500/35 transition-all duration-300 overflow-hidden cursor-default"
            >
              {/* Subtle top-edge amber glow on hover */}
              <div
                className="absolute top-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: `linear-gradient(90deg, transparent, ${svc.accent}88, transparent)` }}
              />

              {/* Service number — top right */}
              <span
                className="absolute top-6 right-7 font-mono text-4xl font-bold leading-none select-none"
                style={{ color: `${svc.accent}12` }}
              >
                {svc.num}
              </span>

              {/* Icon badge */}
              <div
                className="w-12 h-12 flex items-center justify-center rounded-xl mb-6 flex-shrink-0"
                style={{ background: `${svc.accent}18`, border: `1px solid ${svc.accent}30` }}
              >
                <i className={`${svc.icon} text-xl`} style={{ color: svc.accent }} />
              </div>

              {/* Title */}
              <h3 className="text-white font-bold text-lg leading-snug mb-3 pr-8">
                {svc.title}
              </h3>

              {/* Description */}
              <p className="text-white/45 text-sm leading-relaxed mb-6">
                {svc.description}
              </p>

              {/* Feature bullets */}
              <ul className="flex flex-col gap-2.5 mb-7">
                {svc.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <span
                      className="w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                      style={{ background: `${svc.accent}18` }}
                    >
                      <i className="ri-check-line text-[10px]" style={{ color: svc.accent }} />
                    </span>
                    <span className="text-white/55 text-xs leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>

              {/* Bottom link */}
              <div className="pt-5 border-t border-white/5">
                <a
                  href="/membership-benefits"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold transition-all duration-200 group-hover:gap-2.5"
                  style={{ color: svc.accent }}
                >
                  Learn more
                  <i className="ri-arrow-right-line text-xs" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* ── Bottom CTA band ── */}
        <div className="mt-10 rounded-2xl border border-amber-500/15 bg-amber-500/5 px-8 py-7 flex flex-col md:flex-row items-center justify-between gap-5">
          <div>
            <p className="text-white font-semibold text-base">
              All four services are included with every membership plan.
            </p>
            <p className="text-white/40 text-sm mt-1">
              Choose the tier that matches your investment goals — we handle the rest.
            </p>
          </div>
          <a
            href="/pricing"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-amber-500 hover:bg-amber-400 text-[#070c14] font-bold rounded-xl transition-colors duration-200 whitespace-nowrap text-sm flex-shrink-0"
          >
            View Membership Plans
            <i className="ri-arrow-right-line" />
          </a>
        </div>

      </div>
    </section>
  );
}
